import Parse from 'parse';
import { InjectionToken, Provider } from '@angular/core';
import { WorkspaceCasesFacade } from './workspace-cases.facade';
import { GET_WORKSPACE_ENCRYPTION_STATE_QUERY } from './workspace-cases.facade';
import {
  type AnalysisStateRepository,
  type CaseRepository,
  type CaseTypeRepository,
  type SampleAssetRepository,
  type SampleRepository,
  type UserRepository,
  UuidGenerator,
  type WorkspaceRepository,
} from '@telespot/domain';
import {
  ParseAnalysisStateRepository,
  ParseCaseRepository,
  ParseCaseTypeRepository,
  ParseSampleAssetRepository,
  ParseSampleRepository,
  ParseUserRepository,
  ParseWorkspaceRepository,
} from '@telespot/infrastructure/parse';
import {
  CountWorkspaceCasesQuery,
  GetCaseAnalysisSummaryQuery,
  GetWorkspaceEncryptionStateQuery,
  ListWorkspaceCasesQuery,
} from '@telespot/management/use-cases';
import { CopyCasesHandler, MoveCasesHandler } from '@telespot/management/use-cases';
import * as uuid from 'uuid';
import {
  WorkspaceCasesEffects,
  WORKSPACE_CASES_CASE_REPOSITORY,
  LIST_WORKSPACE_CASES_QUERY,
  COUNT_WORKSPACE_CASES_QUERY,
  GET_CASE_ANALYSIS_SUMMARY_QUERY,
  WORKSPACE_CASES_MOVE_HANDLER,
  WORKSPACE_CASES_COPY_HANDLER,
} from '../../../stores/workspace-cases.effects';
import { WorkspaceCasesState } from '../../../stores/workspace-cases.state';
import { rolesStoreProviders } from '@telespot/authentication/ui';

const WORKSPACE_CASES_ANALYSIS_STATE_REPOSITORY = new InjectionToken<AnalysisStateRepository>(
  'WORKSPACE_CASES_ANALYSIS_STATE_REPOSITORY'
);
const WORKSPACE_CASES_USER_REPOSITORY = new InjectionToken<UserRepository>('WORKSPACE_CASES_USER_REPOSITORY');
const WORKSPACE_CASES_WORKSPACE_REPOSITORY = new InjectionToken<WorkspaceRepository>(
  'WORKSPACE_CASES_WORKSPACE_REPOSITORY'
);
const WORKSPACE_CASES_CASE_TYPE_REPOSITORY = new InjectionToken<CaseTypeRepository>(
  'WORKSPACE_CASES_CASE_TYPE_REPOSITORY'
);
const WORKSPACE_CASES_SAMPLE_ASSET_REPOSITORY = new InjectionToken<SampleAssetRepository>(
  'WORKSPACE_CASES_SAMPLE_ASSET_REPOSITORY'
);
const WORKSPACE_CASES_SAMPLE_REPOSITORY = new InjectionToken<SampleRepository>('WORKSPACE_CASES_SAMPLE_REPOSITORY');

export function workspaceCasesProviders(): Provider[] {
  return [
    {
      provide: WORKSPACE_CASES_CASE_REPOSITORY,
      useFactory: () => new ParseCaseRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASES_ANALYSIS_STATE_REPOSITORY,
      useFactory: () => new ParseAnalysisStateRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASES_USER_REPOSITORY,
      useFactory: () => new ParseUserRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASES_WORKSPACE_REPOSITORY,
      useFactory: () => new ParseWorkspaceRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASES_SAMPLE_ASSET_REPOSITORY,
      useFactory: () => new ParseSampleAssetRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: WORKSPACE_CASES_SAMPLE_REPOSITORY,
      useFactory: () => new ParseSampleRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: LIST_WORKSPACE_CASES_QUERY,
      useFactory: (caseRepository: CaseRepository) => new ListWorkspaceCasesQuery(caseRepository),
      deps: [WORKSPACE_CASES_CASE_REPOSITORY],
    },
    {
      provide: COUNT_WORKSPACE_CASES_QUERY,
      useFactory: (caseRepository: CaseRepository) => new CountWorkspaceCasesQuery(caseRepository),
      deps: [WORKSPACE_CASES_CASE_REPOSITORY],
    },
    {
      provide: GET_CASE_ANALYSIS_SUMMARY_QUERY,
      useFactory: (analysisStateRepository: AnalysisStateRepository, userRepository: UserRepository) =>
        new GetCaseAnalysisSummaryQuery(analysisStateRepository, userRepository),
      deps: [WORKSPACE_CASES_ANALYSIS_STATE_REPOSITORY, WORKSPACE_CASES_USER_REPOSITORY],
    },
    {
      provide: WORKSPACE_CASES_MOVE_HANDLER,
      useFactory: (workspaceRepository: WorkspaceRepository, caseRepository: CaseRepository) =>
        new MoveCasesHandler(workspaceRepository, caseRepository),
      deps: [WORKSPACE_CASES_WORKSPACE_REPOSITORY, WORKSPACE_CASES_CASE_REPOSITORY],
    },
    {
      provide: WORKSPACE_CASES_COPY_HANDLER,
      useFactory: (
        workspaceRepository: WorkspaceRepository,
        caseRepository: CaseRepository,
        sampleAssetRepository: SampleAssetRepository,
        sampleRepository: SampleRepository
      ) =>
        new CopyCasesHandler(
          workspaceRepository,
          caseRepository,
          sampleAssetRepository,
          sampleRepository,
          {
            generate: () => uuid.v4(),
            validate: (id: string) => {
              if (!uuid.validate(id) || uuid.version(id) !== 4) throw new Error('Invalid UUID value or version');
            },
          } as UuidGenerator
        ),
      deps: [
        WORKSPACE_CASES_WORKSPACE_REPOSITORY,
        WORKSPACE_CASES_CASE_REPOSITORY,
        WORKSPACE_CASES_SAMPLE_ASSET_REPOSITORY,
        WORKSPACE_CASES_SAMPLE_REPOSITORY,
      ],
    },
    {
      provide: WORKSPACE_CASES_CASE_TYPE_REPOSITORY,
      useFactory: () => new ParseCaseTypeRepository(Parse, { useMasterKey: false }),
    },
    {
      provide: GET_WORKSPACE_ENCRYPTION_STATE_QUERY,
      useFactory: (workspaceRepository: WorkspaceRepository, caseTypeRepository: CaseTypeRepository) =>
        new GetWorkspaceEncryptionStateQuery(workspaceRepository, caseTypeRepository),
      deps: [WORKSPACE_CASES_WORKSPACE_REPOSITORY, WORKSPACE_CASES_CASE_TYPE_REPOSITORY],
    },
    WorkspaceCasesState,
    WorkspaceCasesEffects,
    WorkspaceCasesFacade,
    ...rolesStoreProviders(),
  ];
}
